import { useEffect, useRef, useState } from 'react';

import type {
  DetailedHTMLProps,
  FC,
  ImgHTMLAttributes,
  ReactElement,
  SyntheticEvent,
  VideoHTMLAttributes,
} from 'react';

type VideoProps = Omit<DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>, 'onEnded'> & {
  AutoplayVideoErrorImg?: ReactElement<DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>>;
  onEnded?: (e?: SyntheticEvent<HTMLVideoElement, Event>) => void;
};

const Video: FC<VideoProps> = ({ autoPlay, AutoplayVideoErrorImg, onEnded, ...props }) => {
  const [isFallbackShown, setIsFallbackShown] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!autoPlay || !video) return;

    if (!video.paused || video.currentTime > 0) return;

    const play = async () => {
      try {
        await video.play();
        setIsFallbackShown(false);
      } catch {
        onEnded?.();
        setIsFallbackShown(true);
      }
    };

    play();
  }, [autoPlay, onEnded]);

  if (isFallbackShown) {
    return AutoplayVideoErrorImg;
  }

  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <video ref={videoRef} onEnded={onEnded} {...props} />;
};

export { Video };
